/**
 * Selective haptic feedback utility — web-equivalent of iOS UIImpactFeedbackGenerator.
 *
 * Strategy: Enable ONLY for high-impact state transitions, not navigation taps.
 * Always guarded against browsers without Vibration API support.
 */

type HapticPattern = 'success' | 'error' | 'warning' | 'impact';

const PATTERNS: Record<HapticPattern, number[]> = {
  success: [10, 50, 30],      // Short double pulse — post published, match confirmed
  error:   [80, 30, 80],      // Two heavy pulses — action failed
  warning: [40],              // Single medium pulse — approaching a limit
  impact:  [15],              // Lightest tap — button acknowledge for critical CTAs
};

/**
 * Trigger haptic feedback.
 * Safe to call on desktop (no-ops silently).
 *
 * @example
 * // Post successfully published
 * triggerHaptic('success');
 *
 * // Recovery code accepted
 * triggerHaptic('success');
 *
 * // Invalid action
 * triggerHaptic('error');
 */
export function triggerHaptic(pattern: HapticPattern = 'impact'): void {
  if (typeof window === 'undefined') return;
  if (!('vibrate' in navigator)) return;
  try {
    navigator.vibrate(PATTERNS[pattern]);
  } catch {
    // Silently ignore — some browsers throw on vibrate() even with API present
  }
}
